import { useApi } from './api';
import { readJsonResponse, type ApiResponse } from './apiResponse';
import type {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  PasswordResetResponse
} from '../types/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export const useUserAdminService = () => {
  const { apiFetch } = useApi();

  const getAllUsers = async (): Promise<ApiResponse<User[]>> => {
    try {
      const response = await apiFetch('/admin/users');
      const result = await readJsonResponse<User[]>(response);
      return {
        ok: result.ok,
        status: result.status,
        data: result.ok ? result.data : [],
        error: result.ok ? undefined : (result.error || 'Error al cargar usuarios')
      };
    } catch {
      return { ok: false, status: 500, data: [], error: 'Error de conexión' };
    }
  };

  const getUserById = async (id: number): Promise<ApiResponse<User | null>> => {
    try {
      const response = await apiFetch(`/admin/users/${id}`);
      const result = await readJsonResponse<User>(response);
      return {
        ok: result.ok,
        status: result.status,
        data: result.ok ? result.data : null,
        error: result.ok ? undefined : (result.error || 'Usuario no encontrado')
      };
    } catch {
      return { ok: false, status: 500, data: null, error: 'Error de conexión' };
    }
  };

  const createUser = async (userData: CreateUserRequest): Promise<ApiResponse<User | null>> => {
    try {
      const payload: Record<string, unknown> = {
        username: userData.username.trim(),
        fullName: userData.fullName.trim(),
        email: userData.email.trim(),
        roles: userData.roles,
        isActive: userData.isActive
      };

      if (userData.password && userData.password.trim().length > 0) {
        payload.password = userData.password;
      }

      if (!userData.password || userData.password.trim().length === 0) {
        payload.sendWelcomeEmail = userData.sendWelcomeEmail ?? true;
      }

      const response = await apiFetch('/admin/users', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      const result = await readJsonResponse<User>(response);
      return {
        ok: result.ok,
        status: result.status,
        data: result.ok ? result.data : null,
        error: result.ok ? undefined : (result.error || 'Error al crear usuario')
      };
    } catch {
      return { ok: false, status: 500, data: null, error: 'Error de conexión' };
    }
  };

  const updateUser = async (id: number, userData: UpdateUserRequest): Promise<ApiResponse<User | null>> => {
    try {
      const payload: Record<string, unknown> = {
        fullName: userData.fullName.trim(),
        email: userData.email.trim(),
        roles: userData.roles,
        isActive: userData.isActive
      };

      if (userData.password && userData.password.trim().length > 0) {
        payload.password = userData.password;
      }

      const response = await apiFetch(`/admin/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      const result = await readJsonResponse<User>(response);
      return {
        ok: result.ok,
        status: result.status,
        data: result.ok ? result.data : null,
        error: result.ok ? undefined : (result.error || 'Error al actualizar usuario')
      };
    } catch {
      return { ok: false, status: 500, data: null, error: 'Error de conexión' };
    }
  };

  const deleteUser = async (id: number): Promise<ApiResponse<void>> => {
    try {
      const response = await apiFetch(`/admin/users/${id}`, { method: 'DELETE' });
      if (response.ok) {
        return { ok: true, status: response.status, data: undefined };
      }
      const result = await readJsonResponse<unknown>(response);
      return {
        ok: false,
        status: result.status,
        data: undefined,
        error: result.error || 'Error al eliminar usuario'
      };
    } catch {
      return { ok: false, status: 500, data: undefined, error: 'Error de conexión' };
    }
  };

  const toggleUserStatus = async (id: number, active: boolean): Promise<ApiResponse<User | null>> => {
    try {
      const response = await apiFetch(`/admin/users/${id}/status?active=${active}`, {
        method: 'PATCH'
      });
      const result = await readJsonResponse<User>(response);
      return {
        ok: result.ok,
        status: result.status,
        data: result.ok ? result.data : null,
        error: result.ok ? undefined : (result.error || 'Error al cambiar estado del usuario')
      };
    } catch {
      return { ok: false, status: 500, data: null, error: 'Error de conexión' };
    }
  };

  const getUsersByRole = async (role: string): Promise<ApiResponse<User[]>> => {
    try {
      const response = await apiFetch(`/users/role/${role}`);
      const result = await readJsonResponse<User[]>(response);
      return {
        ok: result.ok,
        status: result.status,
        data: result.ok ? (Array.isArray(result.data) ? result.data : []) : [],
        error: result.ok ? undefined : (result.error || 'Error al cargar usuarios')
      };
    } catch {
      return { ok: false, status: 500, data: [], error: 'Error de conexión' };
    }
  };

  const sendPasswordSetupEmail = async (email: string): Promise<ApiResponse<PasswordResetResponse | null>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const result = await readJsonResponse<PasswordResetResponse>(response);
      return {
        ok: result.ok,
        status: result.status,
        data: result.ok ? result.data : null,
        error: result.ok ? undefined : (result.error || 'Error al enviar email')
      };
    } catch {
      return { ok: false, status: 500, data: null, error: 'Error de conexión' };
    }
  };

  const getAllUsersSimple = async (): Promise<User[]> => {
    const result = await getAllUsers();
    if (!result.ok) {
      throw new Error(result.error || 'Error al cargar usuarios');
    }
    return result.data;
  };

  const createUserSimple = async (userData: CreateUserRequest): Promise<User> => {
    const result = await createUser(userData);
    if (!result.ok || !result.data) {
      throw new Error(result.error || 'Error al crear usuario');
    }
    return result.data;
  };

  const updateUserSimple = async (id: number, userData: UpdateUserRequest): Promise<User> => {
    const result = await updateUser(id, userData);
    if (!result.ok || !result.data) {
      throw new Error(result.error || 'Error al actualizar usuario');
    }
    return result.data;
  };

  const deleteUserSimple = async (id: number): Promise<void> => {
    const result = await deleteUser(id);
    if (!result.ok) {
      throw new Error(result.error || 'Error al eliminar usuario');
    }
  };

  const sendPasswordSetupEmailSimple = async (email: string): Promise<PasswordResetResponse> => {
    const result = await sendPasswordSetupEmail(email);
    if (!result.ok || !result.data) {
      throw new Error(result.error || 'Error al enviar email');
    }
    return result.data;
  };

  const getUsersByRoleSimple = async (role: string): Promise<User[]> => {
    const result = await getUsersByRole(role);
    if (!result.ok) {
      throw new Error(result.error || 'Error al cargar usuarios');
    }
    return result.data;
  };

  return {
    getAllUsers,
    getUserById,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    getUsersByRole,
    sendPasswordSetupEmail,
    getAllUsersSimple,
    createUserSimple,
    updateUserSimple,
    deleteUserSimple,
    sendPasswordSetupEmailSimple,
    getUsersByRoleSimple
  };
};
